import type { RuntimeConfig } from '../config';
import { APIError, LobbyService } from '../network/LobbyService';
import { RealtimeClient, type ConnectionState, type SnapshotDeliveryDiagnostics } from '../network/RealtimeClient';
import type { Credentials, Lobby, Snapshot } from '../network/protocol';
import type { IdentityService, PlayerProfile } from '../network/IdentityService';
import { SocialService, type FriendContact } from '../network/SocialService';
import { cosmeticCSSColor, cosmeticVisual } from '../game/content/cosmetics';
import { crewInviteMessage } from './CrewInvite';
import { presentFriendPresence } from './FriendPresence';
import { GameUpdateNotice } from './GameUpdateNotice';
import { InviteLink } from './InviteLink';

export interface LobbyFlowEvents {
  onLobbyEnter(): void;
  onLobbyLeave(): void;
  onMatchStart(client: RealtimeClient, credentials: Credentials, lobby: Lobby): void;
  onMatchDeparture?(): void;
  onMatchPreparationCancelled?(): void;
  onSnapshot(snapshot: Snapshot): void;
  onSnapshotDelivery(diagnostics: SnapshotDeliveryDiagnostics): void;
  onConnectionState(state: ConnectionState): void;
  onIdentityDeparture(): void;
}

type DepartureMode = 'invite' | 'private' | 'quickplay' | 'training';

export class LobbyFlow {
  private readonly service: LobbyService;
  private inviteCode: string | null;
  private realtime: RealtimeClient | null = null;
  private credentials: Credentials | null = null;
  private currentLobby: Lobby | null = null;
  private startingMatch = false;
  private requestPending = false;
  private departurePending: Promise<void> | null = null;
  private departureRevision = 0;
  private readyIntent: boolean | null = null;
  private credentialOwnerID = '';

  private readonly overlay: HTMLElement;
  private readonly nameInput: HTMLInputElement;
  private readonly createButton: HTMLButtonElement;
  private readonly quickplayButton: HTMLButtonElement;
  private readonly trainingButton: HTMLButtonElement;
  private readonly joinContext: HTMLElement;
  private readonly error: HTMLElement;
  private readonly players: HTMLElement;
  private readonly code: HTMLElement;
  private readonly capacity: HTMLElement;
  private readonly readyButton: HTMLButtonElement;
  private readonly connection: HTMLElement;
  private readonly departure: HTMLElement;
  private readonly eyebrow: HTMLElement;
  private readonly title: HTMLElement;
  private readonly inviteLink: InviteLink;
  private readonly inviteFriendsButton: HTMLButtonElement;
  private readonly friendInvites: HTMLElement;
  private readonly friendInviteList: HTMLElement;
  private readonly friendInviteStatus: HTMLOutputElement;
  private readonly social: SocialService;
  private readonly chat: HTMLElement;
  private readonly chatInput: HTMLInputElement;
  private readonly chatSend: HTMLButtonElement;
  private renderedChatID = 0;
  private departureTimer = 0;
  private friendInviteBusy = false;
  private readonly invitedFriends = new Set<string>();
  private readonly updateNotice: GameUpdateNotice;

  public constructor(
    root: HTMLElement,
    private readonly config: RuntimeConfig,
    private readonly identity: IdentityService,
    private readonly events: LobbyFlowEvents,
  ) {
    this.service = new LobbyService(config, () => identity.authorization());
    this.social = new SocialService(config, identity);
    this.inviteCode = codeFromPath(location.pathname);
    this.overlay = required(root, '[data-lobby-overlay]');
    this.overlay.hidden = true;
    this.nameInput = required(root, '[data-display-name]');
    this.createButton = required(root, '[data-create-lobby]');
    this.quickplayButton = required(root, '[data-quickplay]');
    this.trainingButton = required(root, '[data-training]');
    this.joinContext = required(root, '[data-join-context]');
    this.error = required(root, '[data-lobby-error]');
    this.players = required(root, '[data-lobby-players]');
    this.code = required(root, '[data-lobby-code]');
    this.capacity = required(root, '[data-lobby-capacity]');
    this.readyButton = required(root, '[data-ready]');
    this.connection = required(root, '[data-connection]');
    this.departure = required(root, '[data-departure]');
    this.eyebrow = required(root, '[data-lobby-eyebrow]');
    this.title = required(root, '[data-lobby-title]');
    const inviteButton = required<HTMLButtonElement>(root, '[data-copy-invite]');
    this.inviteLink = new InviteLink({
      button: inviteButton,
      fallback: required<HTMLElement>(root, '[data-invite-link-fallback]'),
      input: required<HTMLInputElement>(root, '[data-invite-link]'),
    }, message => {
      if (message) this.showError(message, false);
      else {
        this.error.textContent = '';
        this.error.classList.remove('is-visible');
      }
    });
    this.inviteFriendsButton = required(root, '[data-invite-friends]');
    this.friendInvites = required(root, '[data-friend-invites]');
    this.friendInviteList = required(root, '[data-friend-invite-list]');
    this.friendInviteStatus = required(root, '[data-friend-invite-status]');
    this.chat = required(root, '[data-lobby-chat]');
    this.chatInput = required(root, '[data-lobby-chat-input]');
    this.chatSend = required(root, '[data-lobby-chat-send]');
    this.updateNotice = new GameUpdateNotice(root, () => {
      const wasInMatch = this.startingMatch;
      void this.leave();
      if (wasInMatch) this.events.onIdentityDeparture();
    });

    this.identity.subscribe(this.handleIdentityProfile.bind(this));
    if (this.inviteCode) {
      setDepartureButton(this.createButton, 'INVITE SIGNAL // ' + this.inviteCode, 'JOIN THIS CREW', '01');
      this.quickplayButton.hidden = true;
      this.trainingButton.hidden = true;
      this.joinContext.hidden = false;
      this.joinContext.textContent = 'INVITE SIGNAL // ' + this.inviteCode;
      const inviteCode = this.inviteCode;
      void this.identity.ready().then((profile) => {
        if (profile?.guest !== false || this.inviteCode !== inviteCode) return;
        const remembered = this.service.recall(inviteCode, profile.id);
        if (remembered && this.identity.current()?.id === profile.id) this.enterLobby(remembered, profile.id);
      });
    }
    this.createButton.addEventListener('click', this.submitPrivate);
    this.quickplayButton.addEventListener('click', this.submitQuickplay);
    this.trainingButton.addEventListener('click', this.submitTraining);
    this.nameInput.addEventListener('keydown', this.onNameKeyDown);
    inviteButton.addEventListener('click', () => void this.inviteLink.copy());
    this.inviteFriendsButton.addEventListener('click', () => void this.openFriendInvites());
    required<HTMLButtonElement>(root, '[data-friend-invites-close]').addEventListener('click', this.closeFriendInvites);
    required<HTMLButtonElement>(root, '[data-leave-lobby]').addEventListener('click', () => { void this.leave(); });
    this.readyButton.addEventListener('click', this.toggleReady);
    this.chatSend.addEventListener('click', this.sendChat);
    this.chatInput.addEventListener('keydown', (event) => { if (event.key === 'Enter') this.sendChat(); });
  }

  public hideForMatch(): void {
    this.inviteLink.setCrew(null);
    this.setCrewVisible(false);
  }

  private handleIdentityProfile(profile: PlayerProfile | null): void {
    this.nameInput.value = profile?.guest === false ? profile.displayName : '';
    if (!this.credentials || !this.credentialOwnerID) return;
    const accountID = profile?.guest === false ? profile.id : '';
    if ((profile !== null && accountID !== this.credentialOwnerID)
      || (!profile && !this.identity.authorization())) {
      const wasInMatch = this.startingMatch;
      void this.leave();
      if (wasInMatch) this.events.onIdentityDeparture();
      return;
    }
    // Retain a reservation during password rotation, but never display the
    // old crew until the server validates this bearer's account ownership.
    this.setCrewVisible(Boolean(accountID) && !this.startingMatch);
  }

  private setCrewVisible(visible: boolean): void {
    const profile = visible && this.credentials ? this.identity.current() : null;
    const owned = profile?.guest === false && profile.id === this.credentialOwnerID;
    // CSS transitions can stall under software rendering. Retired/private
    // content must leave layout/accessibility immediately, not after a fade.
    this.overlay.hidden = !owned;
    if (owned) this.overlay.classList.add('is-visible');
    else this.overlay.classList.remove('is-visible');
  }

  public queueQuickplay(): void {
    this.submit('quickplay');
  }

  public queueTraining(): void {
    this.submit('training');
  }

  public readonly leave = (): Promise<void> => {
    const credentials = this.credentials;
    if (!credentials) return this.departurePending ?? Promise.resolve();
    const wasStartingMatch = this.startingMatch;
    this.createButton.disabled = true;
    this.quickplayButton.disabled = true;
    this.trainingButton.disabled = true;
    const revision = ++this.departureRevision;
    this.service.forget(credentials.lobbyCode);
    this.realtime?.dispose();
    this.realtime = null;
    this.credentials = null;
    this.credentialOwnerID = '';
    this.currentLobby = null;
    this.inviteLink.setCrew(null);
    this.startingMatch = false;
    if (wasStartingMatch) this.events.onMatchDeparture?.();
    this.readyIntent = null;
    this.renderedChatID = 0;
    this.players.replaceChildren();
    this.chat.replaceChildren();
    this.chatInput.value = '';
    this.friendInviteList.replaceChildren();
    this.invitedFriends.clear();
    this.closeFriendInvites();
    window.clearInterval(this.departureTimer);
    this.departureTimer = 0;
    this.setCrewVisible(false);
    history.replaceState({}, '', '/');
    this.inviteCode = null;
    this.quickplayButton.hidden = false;
    this.trainingButton.hidden = false;
    setDepartureButton(this.createButton, 'PRIVATE RUN', 'CREATE CREW', '01');
    setDepartureButton(this.quickplayButton, 'PUBLIC MATCHMAKING // 12–20', 'JOIN RANDOM GAME', '02');
    setDepartureButton(this.trainingButton, 'LIVE PRACTICE // 4 YARD BOTS', 'SOLO TRAINING', '03');
    this.joinContext.hidden = true;
    const departure = this.service.leave(credentials).catch(() => undefined).then(() => {
      if (this.departureRevision !== revision) return;
      this.departurePending = null;
      if (!this.credentials) {
        this.events.onLobbyLeave();
        if (!this.requestPending) {
          this.createButton.disabled = false;
          this.quickplayButton.disabled = false;
          this.trainingButton.disabled = false;
        }
      }
    });
    this.departurePending = departure;
    return departure;
  };

  private readonly submitPrivate = (): void => this.submit(this.inviteCode ? 'invite' : 'private');
  private readonly submitQuickplay = (): void => this.submit('quickplay');
  private readonly submitTraining = (): void => this.submit('training');

  private submit(mode: DepartureMode): void {
    if (this.requestPending || this.departurePending) return;
    this.requestPending = true;
    // Stop public presence before admission can reserve the account's seat.
    // This also cancels a poll that would otherwise race the server-owned
    // Station exclusion and surface an expected 409 as a browser error.
    this.events.onLobbyEnter();
    this.createButton.disabled = true;
    this.quickplayButton.disabled = true;
    this.trainingButton.disabled = true;
    const activeButton = mode === 'training' ? this.trainingButton : mode === 'quickplay' ? this.quickplayButton : this.createButton;
    setDepartureButton(
      activeButton,
      mode === 'training' ? 'AUTHORITATIVE PRACTICE' : mode === 'quickplay' ? 'PUBLIC MATCHMAKING' : mode === 'invite' ? 'INVITE SIGNAL' : 'PRIVATE RUN',
      mode === 'training' ? 'STAGING YARD BOTS…' : mode === 'quickplay' ? 'JOINING RANDOM RUN…' : mode === 'invite' ? 'CATCHING TRAIN…' : 'OPENING SIGNAL…',
      mode === 'training' ? '03' : mode === 'quickplay' ? '02' : '01',
    );
    let ownerID = '';
    void this.identity.ready().then((profile) => {
      if (profile?.guest !== false) throw new APIError('Create an account or log in before entering the Station.', 'account_required', 401);
      ownerID = profile.id;
      const displayName = profile.displayName;
      return mode === 'invite' && this.inviteCode
        ? this.service.join(this.inviteCode, displayName)
        : mode === 'quickplay'
          ? this.service.quickplay(displayName)
          : mode === 'training' ? this.service.training(displayName) : this.service.create(displayName);
    }).then(async (response) => {
      if (!ownerID || this.identity.current()?.id !== ownerID) {
        await this.service.leave(response.credentials).catch(() => undefined);
        throw new APIError('The account session changed before boarding. Try again.', 'session_changed', 409);
      }
      this.service.remember(response.credentials, ownerID);
      history.replaceState({}, '', '/join/' + response.credentials.lobbyCode);
      this.render(response.lobby);
      this.enterLobby(response.credentials, ownerID);
    }).catch((error: unknown) => {
      this.showError(error instanceof APIError ? error.message : 'The yard signal failed.');
      if (!this.credentials) this.events.onLobbyLeave();
    }).finally(() => {
      this.requestPending = false;
      const disabled = this.departurePending !== null;
      this.createButton.disabled = disabled;
      this.quickplayButton.disabled = disabled;
      this.trainingButton.disabled = disabled;
      setDepartureButton(
        this.createButton,
        this.inviteCode ? 'INVITE SIGNAL // ' + this.inviteCode : 'PRIVATE RUN',
        this.inviteCode ? 'JOIN THIS CREW' : 'CREATE CREW',
        '01',
      );
      setDepartureButton(this.quickplayButton, 'PUBLIC MATCHMAKING // 12–20', 'JOIN RANDOM GAME', '02');
      setDepartureButton(this.trainingButton, 'LIVE PRACTICE // 4 YARD BOTS', 'SOLO TRAINING', '03');
    });
  }

  private enterLobby(credentials: Credentials, ownerID: string): void {
    this.realtime?.dispose();
    this.credentials = credentials;
    this.events.onLobbyEnter();
    this.credentialOwnerID = ownerID;
    this.startingMatch = false;
    this.readyIntent = null;
    this.setCrewVisible(true);
    window.clearInterval(this.departureTimer);
    this.departureTimer = window.setInterval(this.updateDeparture, 250);
    this.code.textContent = credentials.lobbyCode;
    this.realtime = new RealtimeClient(this.config, credentials, {
      onState: (state) => {
        this.connection.dataset.state = state;
        this.connection.textContent = connectionLabel(state);
        this.events.onConnectionState(state);
      },
      onLobby: (lobby) => {
        this.render(lobby);
        this.maybeStartMatch(lobby);
      },
      onSnapshot: (snapshot) => {
        this.events.onSnapshot(snapshot);
        if ((snapshot.phase === 'starting' || snapshot.phase === 'active' || snapshot.phase === 'ending') && this.currentLobby) {
          const liveLobby: Lobby = { ...this.currentLobby, phase: snapshot.phase,
            startsAt: snapshot.phase === 'starting' ? this.currentLobby.startsAt : undefined };
          this.currentLobby = liveLobby;
          this.maybeStartMatch(liveLobby);
        }
      },
      onSnapshotDelivery: (diagnostics) => this.events.onSnapshotDelivery(diagnostics),
      onError: (message) => {
        this.readyIntent = null;
        if (this.currentLobby) this.render(this.currentLobby);
        this.showError(message);
      },
      onSessionExpired: (authorization) => this.identity.expireAuthorization(authorization),
      onUpdateRequired: () => this.updateNotice.show(),
    }, async () => {
      const authorization = this.identity.authorization();
      if (!authorization) throw new APIError('Log in again before boarding.', 'session_expired', 401);
      try {
        const grant = await this.service.realtimeGrant(credentials);
        return { authorization, grant: grant.grant };
      } catch (error) {
        // A newly published Pages bundle may reach the previous API for a few
        // minutes. Its v1 fallback keeps that deployment window playable; a
        // current API never returns 404 for this authenticated route.
        if (error instanceof APIError && error.status === 404 && error.code === 'not_found') {
          return { authorization, grant: null };
        }
        if (error instanceof APIError && error.code === 'session_expired') this.identity.expireAuthorization(authorization);
        throw error;
      }
    });
    this.realtime.connect();
  }

  private maybeStartMatch(lobby: Lobby): void {
    if ((lobby.phase !== 'starting' && lobby.phase !== 'active' && lobby.phase !== 'ending') || this.startingMatch || !this.realtime || !this.credentials) return;
    this.startingMatch = true;
    this.events.onMatchStart(this.realtime, this.credentials, lobby);
  }

  private render(lobby: Lobby): void {
    if (this.startingMatch && lobby.phase === 'waiting') {
      this.startingMatch = false;
      this.events.onMatchPreparationCancelled?.();
      this.setCrewVisible(true);
    }
    this.currentLobby = lobby;
    this.code.textContent = lobby.code;
    const quickplay = lobby.mode === 'quickplay';
    const training = lobby.mode === 'training';
    const botField = quickplay || training;
    const bots = lobby.players.filter((player) => player.bot).length;
    this.eyebrow.textContent = training ? 'AUTHORITATIVE PRACTICE // LIVE SERVER' : quickplay ? 'PUBLIC MATCHMAKING // BOT BACKFILL' : 'PRIVATE FREIGHT SIGNAL';
    this.title.textContent = training ? 'SOLO DRILL' : quickplay ? 'RANDOM RUN' : 'CREW';
    this.inviteLink.setCrew(botField || this.startingMatch ? null : lobby.code);
    this.inviteFriendsButton.hidden = botField;
    this.capacity.textContent = String(lobby.players.length) + '/' + String(lobby.maxPlayers) + (bots > 0 ? ` // ${bots} YARD BOTS` : '');
    this.players.replaceChildren();
    for (const player of lobby.players) {
      const row = document.createElement('li');
      if (player.id === this.credentials?.playerId) row.classList.add('is-you');
      const identity = document.createElement('span');
      const signal = document.createElement('i');
      signal.className = 'lobby-suit-signal';
      signal.dataset.connected = String(player.connected);
      if (player.cosmetic) signal.dataset.cosmetic = player.cosmetic;
      const visual = cosmeticVisual(player.cosmetic);
      signal.style.setProperty('--lobby-suit', cosmeticCSSColor(visual?.suit ?? 0x526166));
      signal.style.setProperty('--lobby-accent', cosmeticCSSColor(visual?.accent ?? 0x68dfcc));
      const name = document.createElement('b');
      name.textContent = player.displayName;
      const you = document.createElement('small');
      you.textContent = player.id === this.credentials?.playerId ? 'YOU' : '';
      identity.append(signal, name, you);
      const status = document.createElement('strong');
      status.textContent = player.bot ? (training ? 'DRILL BOT' : 'YARD BOT') : player.ready ? 'READY' : player.connected ? 'BOARDING' : 'RECONNECTING';
      status.dataset.ready = String(player.ready);
      row.append(identity, status);
      this.players.append(row);
    }
    const me = lobby.players.find((player) => player.id === this.credentials?.playerId);
    if (this.readyIntent !== null && me?.ready === this.readyIntent) this.readyIntent = null;
    const readyPending = this.readyIntent !== null;
    this.readyButton.hidden = botField;
    this.readyButton.textContent = readyPending
      ? this.readyIntent ? 'SIGNALING READY…' : 'STANDING DOWN…'
      : me?.ready ? 'NOT READY' : 'READY UP';
    this.readyButton.classList.toggle('is-ready', me?.ready === true);
    this.readyButton.dataset.pending = String(readyPending);
    this.readyButton.disabled = readyPending || lobby.phase === 'active' || lobby.phase === 'ending';
    this.updateDeparture();
    this.renderChat(lobby);
  }

  private renderChat(lobby: Lobby): void {
    const messages = lobby.chat ?? [];
    const lastID = messages.at(-1)?.id ?? 0;
    if (lastID === this.renderedChatID) return;
    const previousScrollTop = this.chat.scrollTop;
    const wasNearBottom = this.renderedChatID === 0
      || this.chat.scrollHeight - this.chat.clientHeight - this.chat.scrollTop < 20;
    this.renderedChatID = lastID;
    this.chat.replaceChildren();
    for (const message of messages) {
      const row = document.createElement('li');
      if (message.playerId === this.credentials?.playerId) row.classList.add('is-you');
      const name = document.createElement('b');
      name.textContent = message.displayName;
      const body = document.createElement('span');
      body.textContent = message.body;
      row.append(name, body);
      this.chat.append(row);
    }
    this.chat.scrollTop = wasNearBottom
      ? this.chat.scrollHeight
      : Math.min(previousScrollTop, Math.max(0, this.chat.scrollHeight - this.chat.clientHeight));
  }

  private readonly sendChat = (): void => {
    const body = this.chatInput.value.trim();
    if (!body) return;
    this.realtime?.chat(body);
    this.chatInput.value = '';
  };

  private readonly toggleReady = (): void => {
    if (this.readyIntent !== null) return;
    const me = this.currentLobby?.players.find((player) => player.id === this.credentials?.playerId);
    if (!me || !this.realtime || !this.currentLobby) return;
    this.readyIntent = !me.ready;
    this.render(this.currentLobby);
    this.realtime.ready(this.readyIntent);
  };

  private async openFriendInvites(): Promise<void> {
    if (this.friendInviteBusy || !this.currentLobby || this.currentLobby.mode !== 'private') return;
    this.friendInvites.hidden = false;
    this.friendInviteStatus.textContent = '';
    this.friendInviteList.replaceChildren(statusRow('TUNING CREW NETWORK…'));
    this.friendInviteBusy = true;
    try {
      const overview = await this.social.overview();
      if (this.friendInvites.hidden) return;
      this.renderFriendInvites(overview.friends);
    } catch (error) {
      this.friendInviteList.replaceChildren(statusRow(error instanceof Error ? error.message : 'CREW NETWORK UNAVAILABLE'));
    } finally {
      this.friendInviteBusy = false;
    }
  }

  private renderFriendInvites(friends: readonly FriendContact[]): void {
    this.friendInviteList.replaceChildren();
    if (!friends.length) {
      this.friendInviteList.append(statusRow('NO CREW LINKS YET // ADD FRIENDS IN STATION COMMS'));
      return;
    }
    for (const friend of friends) {
      const row = document.createElement('li');
      const identity = document.createElement('span');
      const name = document.createElement('b');
      name.textContent = friend.displayName;
      const presence = document.createElement('small');
      const presentation = presentFriendPresence(friend);
      presence.textContent = presentation.status;
      presence.dataset.presence = presentation.presence;
      identity.append(name, presence);
      const invite = document.createElement('button');
      invite.type = 'button';
      invite.textContent = this.invitedFriends.has(friend.userId) ? 'SENT' : 'SEND INVITE';
      invite.setAttribute('aria-label', this.invitedFriends.has(friend.userId)
        ? `Invite sent to ${friend.displayName}`
        : `Send invite to ${friend.displayName}`);
      invite.disabled = this.invitedFriends.has(friend.userId);
      invite.addEventListener('click', () => void this.inviteFriend(friend, invite));
      row.append(identity, invite);
      this.friendInviteList.append(row);
    }
  }

  private async inviteFriend(friend: FriendContact, button: HTMLButtonElement): Promise<void> {
    const lobby = this.currentLobby;
    if (!lobby || lobby.mode !== 'private' || this.invitedFriends.has(friend.userId)) return;
    button.disabled = true;
    button.textContent = 'SENDING…';
    try {
      await this.social.send(friend.userId, crewInviteMessage(lobby.code));
      if (this.currentLobby?.code !== lobby.code) return;
      this.invitedFriends.add(friend.userId);
      button.textContent = 'SENT';
      button.setAttribute('aria-label', `Invite sent to ${friend.displayName}`);
      this.friendInviteStatus.textContent = `BOARDING SIGNAL SENT TO ${friend.displayName.toUpperCase()}.`;
    } catch (error) {
      button.disabled = false;
      button.textContent = 'RETRY';
      this.friendInviteStatus.textContent = error instanceof Error ? error.message : 'INVITE SIGNAL FAILED.';
    }
  }

  private readonly closeFriendInvites = (): void => {
    this.friendInvites.hidden = true;
    this.friendInviteStatus.textContent = '';
  };

  private readonly onNameKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      if (this.inviteCode) this.submitPrivate();
      else this.submitQuickplay();
    }
  };

  private readonly updateDeparture = (): void => {
    const lobby = this.currentLobby;
    if (!lobby) return;
    if (lobby.phase === 'active' || lobby.phase === 'ending') {
      this.departure.textContent = 'TRAIN IN MOTION';
      return;
    }
    if (lobby.mode === 'training') {
      this.departure.textContent = lobby.phase === 'starting'
        ? `DRILL FIELD STAGED // LAUNCHING IN ${secondsUntil(lobby.startsAt)}`
        : 'STAGING FOUR YARD BOTS ON THE LIVE TRAIN';
      return;
    }
    if (lobby.mode !== 'quickplay') {
      this.departure.textContent = lobby.phase === 'starting'
        ? `ALL ABOARD // DEPARTING IN ${secondsUntil(lobby.startsAt)}`
        : 'THE RUN STARTS WHEN EVERYONE IS READY';
      return;
    }
    if (lobby.phase === 'starting') {
      this.departure.textContent = `ROSTER LOCKED // EVERYONE LAUNCHES IN ${secondsUntil(lobby.startsAt)}`;
      return;
    }
    const readyPlayers = lobby.players.filter((player) => player.connected).length;
    const needed = Math.max(0, (lobby.minPlayers ?? 12)-readyPlayers);
    this.departure.textContent = `SEARCHING FOR RUNNERS // ${needed} SLOTS OPEN // YARD BOTS IN ${secondsUntil(lobby.fillsAt)}`;
  };

  private showError(message: string, danger = true): void {
    if (!this.overlay.classList.contains('is-visible')) {
      this.joinContext.hidden = false;
      this.joinContext.textContent = message;
      this.joinContext.dataset.danger = String(danger);
      window.setTimeout(() => {
        if (this.inviteCode) {
          this.joinContext.textContent = 'INVITE SIGNAL // ' + this.inviteCode;
          this.joinContext.dataset.danger = 'false';
        } else {
          this.joinContext.hidden = true;
        }
      }, 4_500);
      return;
    }
    this.error.textContent = message;
    this.error.dataset.danger = String(danger);
    this.error.classList.add('is-visible');
    window.setTimeout(() => this.error.classList.remove('is-visible'), 4_500);
  }
}

function codeFromPath(path: string): string | null {
  const match = /^\/join\/([A-Za-z0-9]{6})\/?$/.exec(path);
  return match?.[1]?.toUpperCase() ?? null;
}

function connectionLabel(state: ConnectionState): string {
  if (state === 'connected') return 'SIGNAL LOCKED';
  if (state === 'reconnecting') return 'RECONNECTING…';
  if (state === 'closed') return 'SIGNAL CLOSED';
  return 'TUNING SIGNAL…';
}

function secondsUntil(value?: string): string {
  if (!value) return '—';
  return String(Math.max(0, Math.ceil((new Date(value).getTime()-Date.now())/1_000))).padStart(2, '0');
}

function setDepartureButton(button: HTMLButtonElement, detail: string, label: string, index: string): void {
  const copy = document.createElement('span');
  const eyebrow = document.createElement('small');
  eyebrow.textContent = detail;
  const title = document.createElement('b');
  title.textContent = label;
  copy.append(eyebrow, title);
  const number = document.createElement('i');
  number.textContent = index;
  button.replaceChildren(copy, number);
}

function statusRow(message: string): HTMLLIElement {
  const row = document.createElement('li');
  row.className = 'is-status';
  row.textContent = message;
  return row;
}

function required<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error('Missing lobby element: ' + selector);
  return element;
}
